import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
} from './markerLayout';
import { DISPLACEMENT_BAND } from './DisplacementPrimitive';
import type { SimulatedResult } from '../../utils/setupCandidates';

/**
 * Diamond markers — auto-graded setup candidates.
 *
 * Marks every candle the rule fires on so they can be reviewed and labelled by
 * hand. Deliberately plain — a small diamond in its own lane — because this
 * sits alongside the displacement chevrons and magnet glyphs and must not be
 * mistaken for a trading signal.
 *
 * Label state is the whole point, so it drives the fill:
 *   strong      solid green, GLOWING — the best setups find your eye first
 *   auto-tagged dashed + glowing in its predicted grade colour — a suggestion,
 *               never drawn solid, so it can't be mistaken for your own call
 *   moderate    solid amber
 *   weak        solid orange
 *   not-ideal   hollow grey, struck through — not a setup at all
 *   missed      solid blue     — the rule didn't fire, you added it by hand
 * Size scales with the score, so strength is readable at a glance, and the
 * verdict is spelled out underneath — size alone tells you how strong, not
 * which way. Auto-tagged markers are drawn hollow rather than solid; they used
 * to carry a robot glyph above as well, which is gone. The selected candidate
 * gets a ring.
 *
 * Every score drawn here is the ENGINE's. There is no second scorer and so
 * nothing to qualify: a bar the engine scored gets a marker, a bar it did not
 * gets no marker at all. The one exception is a bar you hand-labelled `missed`
 * — the engine REJECTED that bar and gave it no score, so its marker draws at
 * the base size with the MISS tag and no number, which is the honest rendering
 * of "you say this should have fired and the rule gave it nothing".
 */

export type CandidateMarkerState =
  | 'unlabelled'
  | 'super-strong'
  | 'strong'
  | 'above-moderate'
  | 'moderate'
  | 'weak'
  | 'not-ideal'
  | 'missed';

export interface CandidateMarker {
  id: string;
  /** Bar time, ms. */
  timestamp: number;
  /** Anchor price — the marker sits just beyond this. */
  high: number;
  low: number;
  direction: 'bull' | 'bear';
  state: CandidateMarkerState;
  selected: boolean;
  /** Suggested by auto-tag rather than labelled by hand. */
  auto?: boolean;
  /**
   * The ENGINE's 0-100 score — drives the diamond's SIZE.
   *
   * Null for a bar the engine rejected and you hand-labelled `missed`. There is
   * no local scorer to fill it in, and a made-up number here is precisely the
   * failure this subsystem paid for: it draws at the base size and prints no
   * figure instead.
   */
  score: number | null;
  /**
   * This bar clears the bot's minConfidence, so the bot would trade it.
   *
   * Grade alone cannot show this: the threshold sits mid-band, so a 55 and a
   * 41 both read the same tier while only one gets taken.
   */
  /**
   * What the simulated trade did, drawn on a second line under the grade tag.
   *
   * `null` when the engine attached no simulation. `unfilled` is NOT a loss —
   * the resting limit expired untouched and no trade happened — so it prints
   * its own word rather than being folded into either column.
   */
  simResult?: SimulatedResult | null;
  /** Signed points of that trade, for the figure beside the word. */
  simPoints?: number | null;
  botWouldTake?: boolean;
  /**
   * Whether the signal fought the 10-bar run it fired into, drawn as a third
   * line under the outcome.
   *
   * `null` when the engine sent no run context — a bar without a full lookback
   * behind it. Absent, not "bars: trending": defaulting would label every early
   * bar as agreeing with a trend nobody measured.
   */
  counterTrend?: boolean | null;
  /**
   * The EMA 9/21 stack's own reading of the same trade, drawn as a fourth line
   * under the run stance.
   *
   * A SECOND opinion, not the same one: `counterTrend` signs the net
   * displacement of the 10 bars before the signal, this compares two moving
   * averages. They can disagree, and the disagreement is the reason both are
   * on the chart.
   *
   * `null` when the engine sent no alignment block — a bar predating the
   * indicator series it retains. Both false is not "aligned": it is an exact
   * tie (ema9 === ema21), which has no stance either.
   */
  stackAligned?: boolean | null;
  /** The stack explicitly runs against the trade. See `stackAligned`. */
  stackOpposed?: boolean | null;
  /**
   * How many of the three EMAs (9, 21, 200) cut the signal candle's BODY, drawn
   * as a fifth line under the stack stance.
   *
   * A THIRD reading, and again its own question: the two lines above ask which
   * way the trend points, this asks whether price is sitting on the averages at
   * all — chop, whichever way they point.
   *
   * `null` when the engine sent no alignment block. Absent, not 0: `body 0/3` is
   * the most reassuring string on the marker and inventing it for an unmeasured
   * bar is the mistake the stance lines already paid for.
   */
  emasThroughBody?: number | null;
}

interface RenderItem {
  x: number;
  y: number;
  state: CandidateMarkerState;
  selected: boolean;
  auto: boolean;
  /** Radius in px, derived from the score. */
  radius: number;
  /** The engine's 0-100, or null on an unscored (rejected, hand-flagged) bar. */
  score: number | null;
  /**
   * What the simulated trade did, drawn on a second line under the grade tag.
   *
   * `null` when the engine attached no simulation. `unfilled` is NOT a loss —
   * the resting limit expired untouched and no trade happened — so it prints
   * its own word rather than being folded into either column.
   */
  simResult?: SimulatedResult | null;
  /** Signed points of that trade, for the figure beside the word. */
  simPoints?: number | null;
  botWouldTake: boolean;
  /** See the marker field. `null` means the engine had no run context. */
  counterTrend?: boolean | null;
  /** See the marker field. `null` means the engine sent no alignment block. */
  stackAligned?: boolean | null;
  /** See the marker field. `null` means the engine sent no alignment block. */
  stackOpposed?: boolean | null;
  /** See the marker field. `null` means the engine sent no alignment block. */
  emasThroughBody?: number | null;
}

const COLORS: Record<CandidateMarkerState, string> = {
  // White is reserved for "no score yet" — no grade uses it.
  unlabelled: '#ffffff',
  // Brighter, cooler green than strong so the top tier separates at a glance.
  'super-strong': '#69f0ae',
  strong: '#00e676',
  // Between strong's green and moderate's amber: it IS the in-between tier.
  'above-moderate': '#9ccc65',
  moderate: '#ffb300',
  weak: '#ff7043',
  'not-ideal': '#78909c',
  missed: '#42a5f5',
};

/**
 * Fill says whose call it is, not how good the setup is — the grade moved to
 * colour and tag, which now come from the MODEL rather than your label.
 *
 * Solid = you labelled this bar by hand. Dashed outline = you have not, so the
 * diamond is showing the model's opinion alone. `not-ideal` stays an outline
 * even though it is hand-labelled: it is struck through and reads as "handled,
 * no", and filling it would make a rejection look like an endorsement.
 */
function isSolid(it: { auto: boolean; state: CandidateMarkerState }): boolean {
  return it.state !== 'not-ideal' && !it.auto;
}

/** Short tag drawn under each diamond — size alone can't say WHICH verdict. */
const TAGS: Record<CandidateMarkerState, string> = {
  unlabelled: '',
  'super-strong': 'SUPER',
  strong: 'STRONG',
  'above-moderate': 'MOD+',
  moderate: 'MOD',
  weak: 'WEAK',
  'not-ideal': 'NO',
  missed: 'MISS',
};

/** The four states that are GRADES. `not-ideal`/`missed` judge, they don't grade. */
const GRADE_STATES: readonly CandidateMarkerState[] = [
  'super-strong',
  'strong',
  'above-moderate',
  'moderate',
  'weak',
];

/**
 * Narrow a grade string from the engine to a state we can actually draw.
 *
 * The engine's `grade` is a plain String over the wire. An unrecognised value
 * would index COLORS to undefined and the diamond would render with no colour
 * at all, so anything unknown falls back rather than half-drawing.
 */
export function asGradeState(grade: string | undefined | null): CandidateMarkerState | undefined {
  return grade && (GRADE_STATES as readonly string[]).includes(grade)
    ? (grade as CandidateMarkerState)
    : undefined;
}

/**
 * Which state the diamond renders as.
 *
 * The grade shown is the ENGINE's, because it is the only one there is. This
 * used to take a `predictedGrade` as well — the chart's own model grade, the
 * fallback for every bar the socket had not covered — and that parameter is
 * exactly where a bar came to render 53/moderate while the bot traded it at
 * 63/strong. There is no fallback now: no engine grade means no grade.
 *
 * `not-ideal` and `missed` outrank the grade. They are not grades: one is you
 * rejecting a rule hit, the other a bar the rule never fired on that you added
 * by hand. A grade painted over either would erase the only record that you
 * judged the bar at all.
 */
export function markerStateFor(opts: {
  /** Your hand label, already normalised. */
  verdict?: CandidateMarkerState;
  /** `grade` off the engine, from the socket or the history endpoint. */
  engineGrade?: string | null;
}): CandidateMarkerState {
  const { verdict, engineGrade } = opts;
  if (verdict === 'not-ideal' || verdict === 'missed') return verdict;
  // White, no tag — "the engine sent a grade this build does not know". Rare,
  // and visibly different from every band rather than guessed into one.
  return asGradeState(engineGrade) ?? 'unlabelled';
}

const TAG_FONT = 8;
/** Gap between the diamond's lower point and the first tag's baseline. */
const TAG_GAP = 8;
/** Vertical pitch between the stacked text lines. */
const TAG_PITCH = TAG_FONT + 2;

/** Stroke width of the boxes drawn round a favourable line, px pre-scale. */
const STANCE_BOX_LINE = 1;

/**
 * Padding between the stance text and the box drawn around it, px pre-scale.
 *
 * Derived rather than chosen, because the room available is not a free
 * parameter: the outcome line's em box ends `TAG_PITCH - TAG_FONT` px above the
 * run line's top, and the border has to fit its padding AND half its stroke
 * into that gap or it draws through the verdict directly above. Two px of
 * layout slack, one px of stroke centred on the edge — 1.5 is what is left.
 *
 * Raise TAG_PITCH and the box breathes; it never has to be retuned by hand.
 *
 * The outcome/run gap is the TIGHTEST one this pad has to fit into, which is why
 * it is the one the derivation uses. The short box (see `stanceBoxLines`) starts
 * one row lower, above the stack line, and the gap there is wider — the run line
 * above it is a point smaller, so its em box ends 3px clear of the stack line's
 * top rather than 2. The same pad therefore clears it with room to spare, and
 * one number keeps both boxes' edges on the same visual rhythm.
 */
const STANCE_BOX_PAD = TAG_PITCH - TAG_FONT - STANCE_BOX_LINE / 2;

/**
 * Clear air between the stance box's bottom stroke and the body box's top one.
 *
 * The two boxes are PEERS, not one box in two parts, so the eye has to be able
 * to see the join. At the default pitch there is no join to see: the stance box
 * ends `STANCE_BOX_PAD + STANCE_BOX_LINE / 2` below the stack line's em box and
 * the body box starts the same distance above the body line's top, and those two
 * edges land on the same pixel — the strokes would overlap exactly and read as
 * one tall box with a bar across it.
 *
 * 2px, matching the gap the stance box already leaves under the outcome line, so
 * the whole block keeps one rhythm.
 */
const BODY_BOX_GAP = 2;

/**
 * Extra leading above the `body` line, on top of TAG_PITCH, buying that gap.
 *
 * Derived, never typed in. The default pitch leaves
 *   TAG_PITCH - (TAG_FONT - 1)
 * between the stack line's em box and the body line's top — the stack line is a
 * point smaller than TAG_FONT, which is what that term is. Out of that the two
 * borders need a pad and a half-stroke EACH, plus the gap:
 *   2 * STANCE_BOX_PAD + STANCE_BOX_LINE + BODY_BOX_GAP
 * and the lead is whatever that comes up short by. Change the pitch, the pad,
 * the stroke or the gap and this follows; = 3 at the numbers above.
 */
const BODY_LEAD =
  2 * STANCE_BOX_PAD + STANCE_BOX_LINE + BODY_BOX_GAP - (TAG_PITCH - (TAG_FONT - 1));

/**
 * Every text line drawn under a diamond, in draw order, with the font it uses.
 *
 * THE list, not a copy of one. The draw path takes each line's baseline and its
 * font from here, and `markerReach` measures the last entry — so a line cannot
 * be added to the chart without the lane that has to hold it growing to match.
 *
 * That drift is exactly the bug this table exists to prevent. The offsets used
 * to be written out by hand at each fillText while markerReach modelled the
 * first tag alone, so every line added after the grade silently spent 10px of
 * the clearance that keeps this block off the displacement chevrons — and the
 * fourth spent the last of it, landing on GLYPH_OFFSET exactly.
 *
 * `lead` is optional extra leading ABOVE a line, on top of TAG_PITCH, for the
 * one case where the pitch alone is not enough: the body line carries a box of
 * its own and the row above it carries the stance box, so the two need room to
 * read as two boxes. Offsets are accumulated down the table rather than
 * multiplied out, so a lead anywhere pushes every line under it — and the lane
 * with them.
 */
const TAG_LINES = [
  { id: 'grade', font: TAG_FONT },
  { id: 'outcome', font: TAG_FONT },
  // The three context lines are a point smaller and unbolded: they are context
  // under a verdict, not a verdict.
  { id: 'run', font: TAG_FONT - 1 },
  { id: 'stack', font: TAG_FONT - 1 },
  { id: 'body', font: TAG_FONT - 1, lead: BODY_LEAD },
] as const;

type TagLineId = (typeof TAG_LINES)[number]['id'];

/**
 * Each line's baseline offset below the diamond's lower point, and its font.
 *
 * Precomputed into a lookup because the draw loop reads it per marker per
 * frame; the table above stays the source those numbers come from.
 */
const TAG_LINE = (() => {
  const out = {} as Record<TagLineId, { y: number; font: number }>;
  let y = TAG_GAP;
  TAG_LINES.forEach((line, i) => {
    if (i > 0) y += TAG_PITCH + ((line as { lead?: number }).lead ?? 0);
    out[line.id] = { y, font: line.font };
  });
  return out;
})();

/**
 * Horizontal breathing room between the widest stance line and the box.
 *
 * Independent of the vertical pad and larger than it: nothing is drawn either
 * side of these lines, so the only constraint is that the box reads as a box
 * rather than a highlight clamped to the letters.
 */
const STANCE_BOX_PAD_X = 3;

/** Corner radius of the box, px pre-scale. Square corners read as a table cell. */
const STANCE_BOX_RADIUS = 2;

/**
 * Pink — the "these two agree" colour, and the last hue on this chart that
 * means nothing yet.
 *
 * This started as teal (#2dd4bf) and that was a real bug, not a taste call: the
 * chart's horizontal level lines are teal and DASHED, so a teal border with a
 * teal dashed line running through it read as chart furniture rather than as an
 * annotation sitting on top of the chart. The replacement has to survive a
 * crowded wheel, so it was picked by elimination over every colour the chart
 * primitives actually draw:
 *
 *   0-45    red / orange / amber   outcome LOSS (#ef4444), run dissent
 *                                  (#f59e0b), weak (#ff7043), moderate
 *                                  (#ffb300), UNFILLED (#facc15)
 *   45-160  yellow / green         outcome WIN (#22c55e) and every grade tier
 *                                  (#69f0ae, #00e676, #9ccc65)
 *   160-200 teal / cyan            the level lines — the bug above
 *   200-240 blue                   missed (#42a5f5)
 *   240-300 violet / magenta       stack dissent (#a78bfa), take-it (#ff00ff)
 *
 * That leaves 320-345, pink, empty. #f472b6 sits at 330 — dead centre of the
 * gap, a full 30 off the take-it magenta on one side and 30 off red on the
 * other, and light (70% L) where the LOSS red is dark and saturated, which is
 * the pair that would actually cost something if confused. Not slate either
 * (#94a3b8 / #64748b): that is the stance text this box wraps, and a border in
 * the text's own colour would read as part of the words.
 */
const STANCE_BOX_COLOUR = '#f472b6';

/**
 * The ground painted inside the box, before the border and before the text.
 *
 * The reason the box exists at all, and the second half of the same bug: the
 * teal dashed level line did not merely clash with the old border, it ran
 * straight THROUGH the two stance lines and `bars: trending` was barely legible
 * where it crossed. Nothing on this marker protected its own text, and any
 * chart line can cross it — levels, VWAP, the EMAs, S/R. So the box stops being
 * an outline and becomes a panel: the words get their own ground.
 *
 * The chart's own background colour (`--theme-chart-background`, whose fallback
 * everywhere in ChartView is #0a0e27 — the same navy TradeMarkerPrimitive's
 * KEYLINE_FALLBACK carries) rather than black, so it reads as a panel cut from
 * the chart rather than a hole punched in it.
 *
 * 0.85 is the number that was tuned rather than reasoned. A level line at full
 * strength keeps 15% of its contrast through this, which is a faint tint across
 * the panel instead of a stripe through the letters — the stance text is slate
 * at 0.75 and clears it easily. Going opaque would make the block a solid slab
 * that dominates the marker and hides the candles behind it, which is a worse
 * chart than the one with the unreadable line.
 *
 * A literal rather than sampled off the live chart: the renderer is handed a
 * bitmap scope, not chart options, and this only has to be dark and in the
 * right family — unlike `keylineColour`, which has to MATCH the background to
 * disappear into it.
 */
const STANCE_BOX_FILL = 'rgba(10, 14, 39, 0.85)';

/** Side of the tick's bounding square, px pre-scale. */
const STANCE_TICK_SIZE = 6;
/**
 * Stroke width of the tick, px pre-scale — deliberately heavier than the box's
 * hairline border.
 *
 * The tick is the mark you scan a COLUMN of diamonds for; the border is what
 * you read once you have stopped on one. At the border's 1px it was two faint
 * scratches next to a rectangle and lost that job entirely. Nearly double the
 * weight on a shape a third of the box's size costs no room and is the whole
 * difference between "there is something beside that box" and not seeing it.
 */
const STANCE_TICK_LINE = 1.8;
/** Gap between the box's left edge and the tick beside it. */
const STANCE_TICK_GAP = 2;

/**
 * How far the DEEPEST ink under the diamond sits from its centre.
 *
 * The bottom of the whole drawn block: the last line's baseline, plus its font
 * height, plus the agreement box's padding and the outer half of its stroke.
 * Exported so the geometry guard can derive the block's depth straight from the
 * draw path's own table, instead of from `markerReach` — which is the thing
 * that guard exists to test.
 *
 * The box term is counted UNCONDITIONALLY even though the box is only drawn on
 * some markers. The lane is one width for every marker on the chart; making it
 * depend on per-marker data would put two diamonds at two different distances
 * from the same candle extreme.
 *
 * Both stance-box variants share their bottom edge — the short one starts lower,
 * it does not end lower — so neither can deepen the block. See `stanceBoxLines`.
 * The BODY box is the deepest thing drawn and it ends exactly where this says:
 * its bottom is the last line's em box plus the same pad, and its half-stroke is
 * the term on the end here.
 */
export function tagBlockDepth(radius: number): number {
  const last = TAG_LINE[TAG_LINES[TAG_LINES.length - 1].id];
  return radius + last.y + last.font + STANCE_BOX_PAD + STANCE_BOX_LINE / 2;
}

// A robot glyph used to sit above every auto-tagged diamond, marking it as the
// machine's call. Removed on request: essentially every diamond is the
// machine's call, so the glyph was on almost all of them and marked nothing —
// it was ink the eye had to skip past on the way to the tag. `auto` still
// decides whether the diamond renders solid (see `isSolid`), which is the
// distinction that was actually doing work.

/** Glow radius (px, pre-scale) on unlabelled markers only. */
const GLOW_BLUR = 7;
/**
 * Glow radius (px, pre-scale) on the stance lines when both readings agree.
 *
 * Under half the diamond's `GLOW_BLUR`, and deliberately so: that one spreads
 * around a 4.5px solid shape, this one sits behind 7px glyphs. At 7 the blur is
 * wider than the strokes it wraps and the words smear into a smudge; at 3 the
 * letters stay crisp and only pick up a halo. Emphasis, not blur.
 */
const STANCE_GLOW_BLUR = 3;
/** Soft halo drawn behind an unlabelled diamond, as a multiple of its radius. */
const HALO_SCALE = 2.6;

/** #rrggbb -> rgba() so gradients can fade a grade colour out to transparent. */
function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * Diamond size scales with the SCORE, continuously rather than in three steps —
 * a 64 and a 66 shouldn't look completely different just because they land
 * either side of a grade boundary.
 *
 * Size and colour are independent channels: colour is the verdict (yours, or
 * the machine's), size is the raw strength. So a big struck-through grey
 * diamond reads as "scored highly, you rejected it anyway" — exactly the
 * disagreement worth seeing while the rule is still being tuned.
 */
export const MIN_RADIUS = 1.8;
export const MAX_RADIUS = 4.5;

/**
 * The trend-stance line under a diamond, or `null` to draw nothing.
 *
 * Extracted from the draw path so the one rule that matters here can be
 * tested: `null`/`undefined` means the engine had no run context — a bar
 * without a full lookback behind it — and must print NOTHING. Defaulting to
 * "bars: trending" would label every early bar as agreeing with a trend nobody
 * measured.
 *
 * Both labels carry the `bars:` prefix, naming the measurement they came from —
 * the run of bars — so this line and the `emas:` line below it are never read
 * as the same opinion stated twice.
 *
 * Amber for counter, slate for trending. Deliberately not green/red: those
 * belong to the outcome line directly above, and a green "bars: trending" over
 * a red LOSS reads as two verdicts disagreeing when only one is a verdict.
 */
export function trendTag(
  counterTrend: boolean | null | undefined
): { text: string; colour: string } | null {
  if (counterTrend === null || counterTrend === undefined) return null;
  return counterTrend
    ? { text: 'bars: counter', colour: '#f59e0b' }
    : { text: 'bars: trending', colour: '#94a3b8' };
}

/**
 * The EMA-stack stance line under a diamond, or `null` to draw nothing.
 *
 * A SECOND trend reading beside `trendTag`'s, and a deliberately different one:
 * that one signs the run of the 10 bars before the signal, this compares the 9
 * and 21 EMAs. They can disagree, and seeing the disagreement without clicking
 * into the popup is the whole reason this line exists — so the label carries
 * the `emas:` prefix, naming its source the way the run line names `bars:`.
 * Without the prefixes two adjacent lines would both just read as "trend".
 *
 * Prints NOTHING in two cases, for the same reason `trendTag` does:
 *  - both booleans false — an exact tie, ema9 === ema21. There is no stance to
 *    report, and the one that would get invented is `emas: trending`, the
 *    reassuring one.
 *  - either argument null/undefined — the engine sent no alignment block, on a
 *    bar predating the indicator series it retains.
 *
 * Violet for opposed, dim slate for aligned: louder when the stack fights the
 * trade, the same way amber/slate works on the run line above. Neither is the
 * green/red of the outcome line — those belong to the verdict, and a green
 * `emas: trending` over a red LOSS reads as two verdicts disagreeing.
 */
export function stackTag(
  stackAligned: boolean | null | undefined,
  stackOpposed: boolean | null | undefined
): { text: string; colour: string } | null {
  if (stackAligned === null || stackAligned === undefined) return null;
  if (stackOpposed === null || stackOpposed === undefined) return null;
  // Opposed is tested FIRST, so an inconsistent pair (both true, which the
  // engine cannot produce — ema9 cannot be both over and under ema21) reads as
  // `emas: counter`. When the data contradicts itself the louder reading wins:
  // an unearned warning costs a second look, an unearned reassurance costs a
  // trade.
  if (stackOpposed) return { text: 'emas: counter', colour: '#a78bfa' };
  if (stackAligned) return { text: 'emas: trending', colour: '#64748b' };
  return null;
}

/** How many EMAs the engine tests against the signal candle's body: 9, 21, 200. */
const BODY_EMA_COUNT = 3;

/**
 * Whether the EMA body count is CLEAR — 0 or 1 of the three cutting the signal
 * candle's body.
 *
 * 0-OR-1 IS NOT A TASTE CALL AND MUST NOT BE TIGHTENED TO 0. It is the engine's
 * own rule, quoted: `trend_alignment.rs` computes `through_body =
 * emas_through_body >= 2` under the comment "Take at 0 or 1 through the body,
 * skip at 2 or 3". The border on this line exists to show, at a glance, which
 * side of THAT line a bar fell on. A stricter frontend threshold would be a
 * second opinion wearing the engine's clothes: bars the engine takes would draw
 * unmarked, and the chart would disagree with the bot about the same rule —
 * which is the exact class of drift `predictedGrade` was deleted for.
 *
 * Anything else — 2, 3, or a value outside the engine's u8 range — is not clear.
 * When the data contradicts itself the louder reading wins, the same way
 * `stackTag` resolves an impossible pair to `counter`: an unearned warning costs
 * a second look, an unearned reassurance costs a trade.
 */
export function bodyIsClear(emasThroughBody: number | null | undefined): boolean {
  if (typeof emasThroughBody !== 'number' || !Number.isFinite(emasThroughBody)) return false;
  const n = Math.round(emasThroughBody);
  return n === 0 || n === 1;
}

/**
 * The EMA body-count line under a diamond, or `null` to draw nothing.
 *
 * How many of the three EMAs (9, 21, 200) cut the signal candle's BODY — a
 * third reading beside the two stances above, and again a different measurement
 * rather than a restatement: `bars:` signs the run into the signal, `emas:`
 * compares the fast pair, and this one asks whether price is sitting ON the
 * averages, which is chop whichever way they point. All three can disagree.
 *
 * Prints NOTHING when the engine sent no alignment block — a bar predating the
 * indicator series it retains. This is the rule that matters here and the reason
 * this function is pure and exported: `body 0/3` is the most reassuring string
 * on the whole marker, "not one average anywhere near this candle", and printing
 * it for a bar nobody measured would be the exact mistake the `bars:` line was
 * fixed for in 6df243b7. A zero is a measurement; absence is not a zero.
 *
 * Slate when clear, amber when not — louder on the unfavourable state, the same
 * split the two lines above make. Amber rather than the `emas:` line's violet
 * deliberately: violet sits on the row IMMEDIATELY above, and two adjacent
 * violet rows read as one claim restated across two lines when they are separate
 * measurements that can disagree. Amber already means "this reading disagrees"
 * on this marker, it is two rows up with a differently-coloured row between, and
 * the `body` label names its own source — so it borrows the vocabulary without
 * borrowing the identity. Never the outcome line's green or red.
 */
export function bodyTag(
  emasThroughBody: number | null | undefined
): { text: string; colour: string } | null {
  if (emasThroughBody === null || emasThroughBody === undefined) return null;
  if (typeof emasThroughBody !== 'number' || !Number.isFinite(emasThroughBody)) return null;
  const n = Math.round(emasThroughBody);
  // Same slate/amber pair the run line uses, so the two states of THIS line
  // differ only in loudness — the count itself is what you read.
  return { text: `body ${n}/${BODY_EMA_COUNT}`, colour: bodyIsClear(n) ? '#94a3b8' : '#f59e0b' };
}

/**
 * Whether BOTH stance lines are present and BOTH read `trending` — the case
 * where the two independent measurements agree the trade runs with the trend.
 *
 * Extracted for the same reason `trendTag` and `stackTag` are: the draw path is
 * canvas and cannot be asserted on, and this is the one rule deciding whether
 * the pair glows.
 *
 * The glow is an AND over what actually got drawn, not over the raw booleans.
 * A line that prints nothing cannot glow, so every case where either tag
 * returns `null` is false here:
 *  - `counterTrend` null/undefined — no run context, the run line is blank.
 *  - `stackAligned`/`stackOpposed` null/undefined — no alignment block.
 *  - both stack booleans false — an exact tie (ema9 === ema21), which `stackTag`
 *    prints nothing for. A tie is not agreement.
 * And either reading counter is false too, which is the point of the rule.
 *
 * Deliberately all-or-nothing across the two lines: the caller glows both or
 * neither. Glowing one alone would read as that measurement being the more
 * true of the pair, which is a claim neither line makes.
 */
export function stancesAgree(
  counterTrend: boolean | null | undefined,
  stackAligned: boolean | null | undefined,
  stackOpposed: boolean | null | undefined
): boolean {
  const run = trendTag(counterTrend);
  const stack = stackTag(stackAligned, stackOpposed);
  if (!run || !stack) return false;
  return counterTrend === false && stackAligned === true && !stackOpposed;
}

/**
 * WHICH stance lines a box wraps. There is no third geometry — a box either
 * starts at the run line or at the stack line, and both end below the stack.
 */
export type StanceBoxLines = 'both' | 'stack';

/**
 * Which lines get boxed, or `null` for no box at all — the whole rule, in one
 * pure function, deliberately ASYMMETRIC.
 *
 *   bars        emas        box
 *   trending    trending    both lines
 *   counter     trending    the emas line only
 *   trending    counter     nothing
 *   counter     counter     nothing
 *   tie / either line absent nothing
 *
 * THE ASYMMETRY IS THE POINT. Do not "simplify" this into `either line reads
 * trending`, and do not make it symmetric — the two off-diagonal cases are not
 * the same event:
 *
 *  - `bars: counter` + `emas: trending` is a PULLBACK inside a trend that is
 *    still structurally intact. The EMA stack — the slower, structural read —
 *    still favours the trade; only the last 10 bars pulled back, which is what
 *    an entry into a trend is supposed to look like. Worth marking, but with a
 *    weaker mark than the both-agree case, so it gets a box round the line that
 *    is actually making the claim and nothing round the line that is not.
 *  - `bars: trending` + `emas: counter` is the reverse and is NOT the mirror of
 *    it: a short-term push AGAINST the structure. Ten bars of momentum do not
 *    repair a stack that is on the wrong side. That gets nothing.
 *
 * Both lines must still be DRAWN for either box, exactly as `stancesAgree`
 * requires. A box around one line and a blank row would read as that
 * measurement being the more true of the pair; on a tie or a missing block
 * there is no second reading to be counter TO, so the pullback story cannot be
 * told and no box is the honest output.
 *
 * `stancesAgree` is untouched and is still the both-lines condition — the glow
 * and other callers ride on it. This adds the third state alongside it rather
 * than widening it.
 */
export function stanceBoxLines(
  counterTrend: boolean | null | undefined,
  stackAligned: boolean | null | undefined,
  stackOpposed: boolean | null | undefined
): StanceBoxLines | null {
  // Presence is judged by what the tags actually PRINT, never by the raw
  // booleans — the same test `stancesAgree` makes, and for the same reason: a
  // line that draws nothing cannot be boxed.
  const run = trendTag(counterTrend);
  const stack = stackTag(stackAligned, stackOpposed);
  if (!run || !stack) return null;
  if (stancesAgree(counterTrend, stackAligned, stackOpposed)) return 'both';
  // The pullback: the run line printed `counter`, the stack line printed
  // `trending`. `stackTag` resolves an inconsistent pair to `counter` already,
  // so re-testing `!stackOpposed` here keeps this in step with the words drawn.
  if (counterTrend === true && stackAligned === true && !stackOpposed) return 'stack';
  return null;
}

/**
 * A border-and-tick drawn round one or more lines, or `null` to draw nothing.
 *
 * Every offset is below the diamond's lower point and in PRE-SCALE px, the same
 * units and the same axis as `TAG_LINE` — the draw path multiplies by `hr`/`vr`
 * on the way out, exactly as it does for the text.
 *
 * SHARED by the stance box and the body box, which are the same mark round
 * different rows: both say "this reading favours the trade". One shape, one
 * `boxGeometry` that builds it and one `paintBox` that draws it, so the two can
 * never drift into looking like two different claims.
 */
export interface MarkerBox {
  /** Top edge (the stroke's centreline), px below the diamond's lower point. */
  top: number;
  /** Bottom edge, same axis. */
  bottom: number;
  /** Half the box's width. It is centred on the marker's x, as the text is. */
  halfWidth: number;
  /** Corner radius. */
  radius: number;
  /** Stroke width. */
  lineWidth: number;
  colour: string;
  /**
   * The semi-opaque ground painted INSIDE the border, before it and before the
   * text, so the stance lines stay legible over whatever crosses them.
   *
   * Carried on the box rather than read as a module constant by the draw path
   * so there is exactly ONE gate on it: no box, no fill. A separately-decided
   * backdrop could come to paint on a marker that has no border around it.
   */
  fill: string;
  /** Tick centre, as an x offset from the marker's x. Negative: to the LEFT. */
  tickX: number;
  /** Tick centre, px below the diamond's lower point. */
  tickY: number;
  /** Side of the tick's bounding square. */
  tickSize: number;
  /** Stroke width of the tick — heavier than the border's. See STANCE_TICK_LINE. */
  tickLine: number;
}

/** The stance box, which additionally records WHICH stance lines it wraps. */
export interface StanceBox extends MarkerBox {
  /**
   * Which lines this box wraps. Carried rather than inferred from `top` so the
   * three states stay a named thing the tests can assert on directly.
   */
  lines: StanceBoxLines;
}

/**
 * The one place a box's shape comes from — every box on this marker, so there is
 * exactly one visual to keep consistent rather than two that have to be checked
 * against each other.
 *
 * `top`/`bottom` are the caller's, because that is the only thing the two boxes
 * differ in. Everything else — ground, border, radius, colour, tick size, tick
 * weight, tick placement — is fixed here.
 *
 * A non-finite or negative width collapses to the padding alone rather than
 * producing a NaN path that silently kills the rest of the frame.
 */
function boxGeometry(top: number, bottom: number, width: number): MarkerBox {
  const halfWidth = (Number.isFinite(width) && width > 0 ? width : 0) / 2 + STANCE_BOX_PAD_X;
  return {
    top,
    bottom,
    halfWidth,
    radius: STANCE_BOX_RADIUS,
    lineWidth: STANCE_BOX_LINE,
    colour: STANCE_BOX_COLOUR,
    // Inside the same rectangle the border traces, so it adds no depth — see
    // `tagBlockDepth`, which is unchanged by it.
    fill: STANCE_BOX_FILL,
    // Outside the box, not inside it. The lines are centred on the marker's x
    // and so is the box; a tick inside the left edge would either sit on the
    // first line's letters or push the box off that centre.
    tickX: -(halfWidth + STANCE_BOX_LINE / 2 + STANCE_TICK_GAP + STANCE_TICK_SIZE / 2),
    // ONE tick per box, centred on it. One per line would say each line is
    // individually ticked; the tick marks the BOX, and the box is the claim.
    // Centring means it tracks whatever rows the box ends up wrapping for free
    // and stays vertically inside the border, so it adds no depth of its own.
    tickY: (top + bottom) / 2,
    tickSize: STANCE_TICK_SIZE,
    tickLine: STANCE_TICK_LINE,
  };
}

/**
 * Geometry for the box round the EMA body count, or `null` for no box.
 *
 * A PEER of the stance box, not a variant of it: it wraps its own single line
 * and answers its own question, and the two can be on screen at once. Same
 * shape, because they are the same kind of mark — "this reading favours the
 * trade" — and `boxGeometry` is literally the same call, so they cannot drift
 * apart. What separates them on screen is the two px of clear air `BODY_BOX_GAP`
 * buys between their strokes; without it the two borders would land on the same
 * pixel and read as one tall box with a bar through it.
 *
 * The condition is `bodyIsClear` and nothing else — 0 or 1 of the three EMAs
 * through the candle's body, the engine's own take/skip line. See `bodyIsClear`
 * for why that must not be tightened to 0.
 *
 * Absent data draws no line, so it can draw no box: `bodyTag` is the gate, the
 * same way `trendTag`/`stackTag` gate the stance box. A box round a row with no
 * text in it would be a claim about a bar nobody measured.
 *
 * `bodyWidth` is the rendered string, measured by the caller in the font the
 * line is actually drawn in and divided back out of device px.
 */
export function bodyBox(
  emasThroughBody: number | null | undefined,
  bodyWidth: number
): MarkerBox | null {
  if (!bodyTag(emasThroughBody)) return null;
  if (!bodyIsClear(emasThroughBody)) return null;
  // Derived from the line table, never restated — the same rule `tagBlockDepth`
  // and `stanceBox` follow. This is the DEEPEST ink on the marker, so a changed
  // pitch, font or lead has to move the lane with it.
  const top = TAG_LINE.body.y - STANCE_BOX_PAD;
  const bottom = TAG_LINE.body.y + TAG_LINE.body.font + STANCE_BOX_PAD;
  return boxGeometry(top, bottom, bodyWidth);
}

/**
 * Geometry for the box round the stance lines, in whichever of its two extents
 * `stanceBoxLines` calls for — or `null` for the cases that get nothing.
 *
 * The glow that used to carry this on its own does not survive a live chart: at
 * 7px a 3px halo behind grey text is invisible while scanning a column, which
 * is the only time it needs to be seen. A hard border does read at that size.
 *
 * The CONDITION is `stanceBoxLines` and nothing else — no second rule bolted on
 * here. In particular two lines both reading `counter` are in sync with each
 * other while the trade runs AGAINST the trend; that is not a box, and the
 * asymmetry that says so lives in one place, up there.
 *
 * SAME VISUAL, both extents: same ground, border, radius, colour and tick. The
 * two states differ only in WHICH lines are inside the rectangle — and since
 * each line names its own source (`bars:` / `emas:`), the reader does not have
 * to decode a box height, they read the words that are in the box. That is a
 * stronger cue than any second colour would be, and there is no free hue left
 * to spend on one: the palette walk in `STANCE_BOX_COLOUR` found exactly one
 * empty band on this chart and this box already sits in it. A second colour
 * would have to be borrowed from take-it magenta or LOSS red, both 30 degrees
 * away, to mark the WEAKER of two states that mean the same direction. The
 * short box is the same claim with one fewer line backing it, so it should look
 * like the same mark, smaller — not like a different mark.
 *
 * Pure, and returning numbers rather than drawing, for the same reason
 * `trendTag`/`stackTag`/`stancesAgree` are: the canvas path cannot be asserted
 * on, so everything with a decision in it lives out here where it can be.
 *
 * `runWidth`/`stackWidth` are the two rendered strings, measured by the caller
 * in the font the lines are actually drawn in and divided back out of device
 * px. Both are passed and this picks: `bars:`/`emas:` are different widths, a
 * two-line box has to hold whichever is longer, and a stack-only box must be
 * sized to the stack line ALONE — sizing it to a wider `bars:` line it does not
 * wrap would hang the border out past its own contents, under a line that is
 * deliberately outside it. A non-finite or negative width collapses to the
 * padding alone rather than producing a NaN path that silently kills the rest
 * of the frame.
 */
export function stanceBox(
  counterTrend: boolean | null | undefined,
  stackAligned: boolean | null | undefined,
  stackOpposed: boolean | null | undefined,
  runWidth: number,
  stackWidth: number
): StanceBox | null {
  const lines = stanceBoxLines(counterTrend, stackAligned, stackOpposed);
  if (!lines) return null;
  const clean = (w: number) => (Number.isFinite(w) && w > 0 ? w : 0);
  const width = lines === 'both' ? Math.max(clean(runWidth), clean(stackWidth)) : clean(stackWidth);
  // Derived from the line table, never from restated offsets — the same rule
  // `tagBlockDepth` follows, and for the same reason: a fifth line, a changed
  // pitch or a changed font must move this box with it.
  //
  // Only the TOP moves between the two extents. The bottom is the stack line's
  // in both, which is what keeps a shorter box from ever reaching deeper than
  // the taller one. Neither reaches the BODY line below: that row carries a lead
  // of its own (see `BODY_BOX_GAP`) so the two boxes' strokes cannot touch.
  const top = (lines === 'both' ? TAG_LINE.run.y : TAG_LINE.stack.y) - STANCE_BOX_PAD;
  const bottom = TAG_LINE.stack.y + TAG_LINE.stack.font + STANCE_BOX_PAD;
  // Same `boxGeometry` the body box uses. The tick is kept on the short box
  // rather than dropped, even though it is the weaker state: it is the mark you
  // find a diamond BY while scanning a column at 7px — that is its entire job,
  // and a box you cannot find is not a weaker mark, it is no mark. The box's
  // extent and the words inside it say which state this is; the tick was never
  // carrying that distinction.
  return { lines, ...boxGeometry(top, bottom, width) };
}

/**
 * A rounded rectangle path via `arcTo`, which every canvas has.
 *
 * Not `ctx.roundRect`: it is recent enough that the DOM lib this project
 * compiles against may not declare it, and a border on a review marker is not
 * worth a build that depends on the TypeScript version.
 */
function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  const rad = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}

/**
 * Paint one box — ground, border, tick — at a marker.
 *
 * SHARED by the stance box and the body box rather than written twice. They are
 * deliberately the same mark ("this reading favours the trade") on different
 * rows, and two copies of this would be two places for one of them to acquire a
 * slightly different alpha, radius or tick weight; at 7px that difference reads
 * as two different kinds of claim, which is exactly what they are not.
 *
 * `yBase` is the diamond's lower point in device px — every offset on the box is
 * measured down from there, in pre-scale px, so this is where `hr`/`vr` go on.
 *
 * Leaves globalAlpha, fillStyle, strokeStyle, lineWidth, lineCap and lineJoin
 * dirty. The caller resets them UNCONDITIONALLY; see the reset in `draw`.
 */
function paintBox(
  ctx: CanvasRenderingContext2D,
  box: MarkerBox,
  x: number,
  yBase: number,
  hr: number,
  vr: number
): void {
  const top = yBase + box.top * vr;
  const bottom = yBase + box.bottom * vr;
  const left = x - box.halfWidth * hr;
  // ONE path, filled and then stroked. The border traces the same rectangle the
  // ground fills, which is what keeps the fill from adding any depth of its own.
  roundedRectPath(
    ctx,
    left,
    top,
    box.halfWidth * 2 * hr,
    bottom - top,
    // Corners scale with the smaller axis so a non-square device ratio cannot
    // round one pair of corners more than the other.
    box.radius * Math.min(hr, vr)
  );

  // Ground FIRST — before the border, and long before the text, which paints on
  // top of it further down. A teal dashed level line used to run straight
  // through `bars: trending`; this is what stops it, and it only works painted
  // underneath everything.
  //
  // globalAlpha is pinned to 1 and the transparency lives in the colour itself:
  // the border below wants a different alpha, and multiplying the two would make
  // the fill's tuned 0.85 depend on whatever the border happens to be set to.
  ctx.globalAlpha = 1;
  ctx.fillStyle = box.fill;
  ctx.fill();

  // Then the border, on the same path. Still a hairline — it has to group and
  // confirm, not compete with the grade and the verdict above it — but no longer
  // as faint as it was: it now sits on the dark ground rather than on the
  // candles, and at 0.5 the pink came through that ground as a muddy mauve and
  // read as chart furniture again, which is the exact complaint.
  ctx.globalAlpha = 0.7;
  ctx.strokeStyle = box.colour;
  ctx.lineWidth = box.lineWidth * hr;
  ctx.stroke();

  // The tick: two strokes, NOT a `✓` glyph. At this size the glyph depends on
  // whichever font the browser substitutes for Arial's missing U+2713 and
  // renders as a smudge as often as a tick; two segments are the same shape at a
  // known size on every machine.
  const tx = x + box.tickX * hr;
  const ty = yBase + box.tickY * vr;
  const halfW = (box.tickSize / 2) * hr;
  const halfH = (box.tickSize / 2) * vr;
  // Heavier and brighter than the box, both. This is the mark you scan a column
  // of diamonds FOR, and it is the smaller of the two shapes — at the border's
  // weight and alpha it disappeared at exactly the zoom where the box is still
  // readable.
  ctx.globalAlpha = 0.95;
  ctx.lineWidth = box.tickLine * hr;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(tx - halfW, ty);
  ctx.lineTo(tx - halfW * 0.25, ty + halfH * 0.8);
  ctx.lineTo(tx + halfW, ty - halfH);
  ctx.stroke();
}

/**
 * How far the diamond BLOCK reaches from its own centre, at a given radius.
 *
 * The diamond is not the tallest thing drawn here and its centre is not the
 * middle of the ink. Three things reach past it, and the reach is applied the
 * same in BOTH directions even though only the text is off-centre — a marker
 * that reserved less room above than below would sit at a different distance
 * from the lane depending on its direction, and the lane has to hold either.
 *
 *   text   = tagBlockDepth(r)         = 4.5 + 58 + 2     = 64.5
 *   halo   = r * HALO_SCALE           = 4.5 * 2.6        = 11.7
 *   glow   = r + GLOW_BLUR            = 4.5 + 7          = 11.5
 *
 * The text term is the five lines (8 + 30 + 3 lead + 10 + 7 = 58) plus a box
 * hanging 2px past the last of them — its padding and the outer half of its
 * stroke. See `tagBlockDepth` for why that is counted on every marker and not
 * only the ones that draw it. It was 51.5 with four lines; the body count added
 * a row, and its own box needed clear air above it, so every diamond now sits
 * 13px further from its candle. Expected, and the price of the extra line.
 *
 * The text term MUST track the draw path, and does so by measuring TAG_LINES
 * rather than restating it. Getting that wrong is not theoretical: this used to
 * read `r + TAG_GAP + TAG_FONT`, the first tag alone, while four lines were
 * being drawn. The block hung 29px further inward than the lane reserved, and
 * the fourth line came to rest on the chevrons at GLYPH_OFFSET 34 — the same
 * collision this comment has described since the first time it happened, when a
 * centre parked at 46 looked clear of a 34px lane and the tag hung back to ~33.
 *
 * The text is drawn on the candle side (y + r + …, screen-down on a bull, which
 * points back at the high), so it is always the term that decides. A robot
 * glyph used to be a fourth term at 16.7; it never set the maximum, so removing
 * it changed no spacing.
 *
 * Radius scales with the score, so the worst case is always MAX_RADIUS.
 */
export function markerReach(radius: number): number {
  return Math.max(
    tagBlockDepth(radius),
    radius * HALO_SCALE,
    radius + GLOW_BLUR,
  );
}

/** Breathing room between the displacement block and the diamond block. */
const LANE_GAP = 6;

/**
 * Lane above/below the bar, clear of WCC rings (12), magnet (28) and — the one
 * that actually bit — the whole displacement block, labels included.
 *
 *   OFFSET = DISPLACEMENT_BAND.outer + LANE_GAP + markerReach(MAX_RADIUS)
 *          = 56                      + 6        + 64.5                    = 126.5
 *          -> 127
 *
 * Derived, not typed in: DISPLACEMENT_BAND is itself computed from
 * DisplacementPrimitive's own constants, so moving GLYPH_OFFSET or growing its
 * range readout pushes this lane out to match. Equally, raising TAG_GAP or
 * MAX_RADIUS — or adding a sixth line to TAG_LINES — widens markerReach and
 * this offset grows to absorb it, which is why the lane sits at 127 rather
 * than the 83 it held when reach still modelled one tag, or the 114 it held at
 * four lines. Every diamond moves further from its candle when a line is added;
 * that is the mechanism working, not a regression. The
 * guard lives in CandidatePrimitive.test.ts — it asserts the two bands stay
 * disjoint in screen space, both directions, at MAX_RADIUS.
 *
 * Fixed rather than conditional on "is there a displacement marker on this
 * bar". Conditional stacking would save ~37px whenever displacement is off, at
 * the cost of feeding one primitive's data into the other, keeping markerAt()'s
 * hit box in sync with it, and handling the frame where the two setters
 * disagree. Not worth it for vertical space on a review marker.
 */
export const OFFSET = Math.ceil(
  DISPLACEMENT_BAND.outer + LANE_GAP + markerReach(MAX_RADIUS),
);

/**
 * The band the diamond block occupies, as distance from the candle extreme —
 * the same axis DISPLACEMENT_BAND uses, so the two are directly comparable.
 */
export function candidateBand(radius: number): { inner: number; outer: number } {
  const reach = markerReach(radius);
  return { inner: OFFSET - reach, outer: OFFSET + reach };
}

/**
 * Null (an unscored bar) draws at the MINIMUM radius, same as a zero.
 *
 * Deliberately the floor and not the middle: size means strength, and a bar the
 * engine declined to score has no strength to claim. Its MISS tag and blue
 * colour are what identify it.
 */
function radiusForScore(score: number | null): number {
  const s =
    typeof score === 'number' && Number.isFinite(score)
      ? Math.min(Math.max(score, 0), 100)
      : 0;
  return MIN_RADIUS + (s / 100) * (MAX_RADIUS - MIN_RADIUS);
}

/**
 * The diamond's whole drawn block for the shared marker layout, relative to the
 * diamond centre: halo / glow above it, every tag line (and their boxes, ticks
 * on the left) below it, down to tagBlockDepth like the lane always reserved.
 * Widths are the strings this marker will actually print.
 */
export function candidateBlock(
  it: Pick<RenderItem, 'radius' | 'state' | 'score' | 'simResult' | 'simPoints' | 'counterTrend' | 'stackAligned' | 'stackOpposed' | 'emasThroughBody'>,
  measure: (text: string, font: string) => number,
): { left: number; right: number; up: number; down: number } {
  const r = it.radius;
  const up = Math.max(r * HALO_SCALE, r + GLOW_BLUR);
  const grade = TAGS[it.state];
  let textW = 0;
  if (grade) textW = Math.max(textW, measure(it.score === null ? grade : `${Math.round(it.score)} ${grade}`, `bold ${TAG_LINE.grade.font}px Arial`));
  if (it.simResult && it.simResult !== 'open' && it.simResult !== 'ambiguous') {
    const pts = typeof it.simPoints === 'number' && it.simPoints !== 0 ? ` ${it.simPoints > 0 ? '+' : ''}${Math.round(it.simPoints)}` : '';
    textW = Math.max(textW, measure(`UNFILLED${pts}`, `bold ${TAG_LINE.outcome.font}px Arial`));
  }
  for (const t of [trendTag(it.counterTrend), stackTag(it.stackAligned, it.stackOpposed), bodyTag(it.emasThroughBody)]) {
    if (t) textW = Math.max(textW, measure(t.text, `${TAG_LINE.run.font}px Arial`));
  }
  const half = Math.max(r * HALO_SCALE, textW / 2 + STANCE_BOX_PAD_X + STANCE_BOX_LINE);
  // The agreement tick hangs off the left of the boxes.
  const tick = STANCE_TICK_GAP + STANCE_TICK_SIZE + STANCE_TICK_LINE;
  return { left: -(half + tick), right: half, up, down: tagBlockDepth(r) };
}

class CandidatePaneRenderer implements IPrimitivePaneRenderer {
  private _items: RenderItem[] = [];

  update(items: RenderItem[]): void {
    this._items = items;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const hr = scope.horizontalPixelRatio as number;
      const vr = scope.verticalPixelRatio as number;

      for (const it of this._items) {
        const x = it.x * hr;
        const y = it.y * vr;
        const r = it.radius * hr;
        const colour = COLORS[it.state];
        const solid = isSolid(it);
        // STRONG glows — that's the whole point of the channel now everything
        // carries a tag: the best setups should find your eye without you
        // reading anything. Unlabelled still glows white on the rare occasion
        // one exists (no score yet).
        const glows =
          it.state === 'super-strong' || it.state === 'strong' || it.state === 'unlabelled';
        const glowColour = it.state === 'unlabelled' ? '#ffffff' : colour;

        ctx.save();

        // Soft halo, drawn first so the diamond sits on top of it.
        if (glows) {
          const halo = ctx.createRadialGradient(x, y, 0, x, y, r * HALO_SCALE);
          // Super-strong glows harder — it's the tier you act on.
          halo.addColorStop(0, hexToRgba(glowColour, it.state === 'super-strong' ? 0.55 : 0.38));
          halo.addColorStop(1, hexToRgba(glowColour, 0));
          ctx.fillStyle = halo;
          ctx.beginPath();
          ctx.arc(x, y, r * HALO_SCALE, 0, Math.PI * 2);
          ctx.fill();
        }

        // No selection ring. Clicking a diamond opens the labeller right on top
        // of it, which already says which one you picked — a white circle at
        // 2.1x the radius only added a big shape over the candles you were
        // trying to read. `selected` is still carried for the review panel.

        // Diamond.
        ctx.beginPath();
        ctx.moveTo(x, y - r);
        ctx.lineTo(x + r, y);
        ctx.lineTo(x, y + r);
        ctx.lineTo(x - r, y);
        ctx.closePath();

        if (solid) {
          // Glow the fill too, not just outlines — strong markers are solid.
          if (glows) {
            ctx.shadowColor = glowColour;
            ctx.shadowBlur = GLOW_BLUR * hr;
          }
          ctx.fillStyle = colour;
          ctx.fill();
          ctx.shadowBlur = 0;
        } else {
          ctx.globalAlpha = it.state === 'not-ideal' ? 0.5 : 1;
          if (glows) {
            // Canvas glow on the stroke itself, on top of the halo.
            ctx.shadowColor = glowColour;
            ctx.shadowBlur = GLOW_BLUR * hr;
          }
          ctx.strokeStyle = colour;
          ctx.lineWidth = (glows ? 1.9 : 1.4) * hr;
          // No dash for unlabelled markers: hollow-vs-solid already says whose
          // call it is, and encoding the same bit twice just made the chart
          // noisier without telling you anything new.
          ctx.stroke();
          ctx.setLineDash([]);
          if (glows) {
            // A bright centre dot: at small zoom the outline alone can vanish
            // into the candles, and this keeps the marker locatable.
            ctx.fillStyle = glowColour;
            ctx.beginPath();
            ctx.arc(x, y, r * 0.3, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.shadowBlur = 0;
        }

        // Tag under the diamond: the score AND the grade, because the grade
        // alone hides how close a bar sat to the next band — a 79 and a 41 both
        // read "MOD" otherwise, and the bot's minConfidence gate works on the
        // number, not the band.
        const grade = TAGS[it.state];
        // No number on an unscored bar. The engine rejected it, so there is
        // nothing to print — and there is no longer a local scorer to ask,
        // which is the point. The grade word alone still says what it is.
        const tag = grade
          ? it.score === null
            ? grade
            : `${Math.round(it.score)} ${grade}`
          : '';
        if (tag) {
          ctx.globalAlpha = 0.95;
          ctx.fillStyle = colour;
          ctx.font = `bold ${TAG_LINE.grade.font * hr}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillText(tag, x, y + r + TAG_LINE.grade.y * vr);
          ctx.globalAlpha = 1;
        }

        // Second line: what the simulated trade DID. On the chart rather than
        // only in the popup, so a run of signals can be read at a glance
        // without clicking each one.
        //
        // Coloured by outcome, not by the grade — the grade already owns the
        // line above, and using its colour here would make a losing
        // super-strong look like a win.
        if (it.simResult && it.simResult !== 'open' && it.simResult !== 'ambiguous') {
          const won = it.simResult === 'win';
          const unfilled = it.simResult === 'unfilled';
          // Closed by the 1pm PT bell rather than by a level. Neither a win
          // nor a loss, and it must not borrow either colour — the previous
          // `else` here would have drawn it as LOSS, which is exactly the
          // conflation the engine keeps out of the win rate.
          const flat = it.simResult === 'flat';
          const pts =
            typeof it.simPoints === 'number' && it.simPoints !== 0
              ? ` ${it.simPoints > 0 ? '+' : ''}${Math.round(it.simPoints)}`
              : '';
          const word = unfilled
            ? 'UNFILLED'
            : flat
              ? `FLAT${pts}`
              : won
                ? `WIN${pts}`
                : `LOSS${pts}`;
          ctx.globalAlpha = unfilled || flat ? 0.7 : 0.95;
          ctx.fillStyle = unfilled
            ? '#facc15'
            : flat
              ? '#94a3b8'
              : won
                ? '#22c55e'
                : '#ef4444';
          ctx.font = `bold ${TAG_LINE.outcome.font * hr}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          // One tag-height below the grade line so the two never collide.
          ctx.fillText(word, x, y + r + TAG_LINE.outcome.y * vr);
          ctx.globalAlpha = 1;
        }

        // Third line: did this signal fight the run it fired into.
        //
        // On the chart because reading a column of WIN/LOSS against trend
        // stance is the comparison being made by eye, and clicking each
        // diamond to find out defeats the point.
        //
        // Dimmer and unbolded, so it stays subordinate to the outcome above
        // it. Amber for counter and grey for trending: NOT green/red, which
        // belong to the outcome line — a green "bars: trending" over a red LOSS
        // would read as two verdicts disagreeing.
        //
        // The label names its source (`bars:`), so it cannot be mistaken for
        // the EMA-stack line drawn below it.
        //
        // Nothing is drawn when the engine sent no run context. A bar without
        // a full lookback has no stance, and printing one would invent it.
        //
        // Both stance lines glow, and get a border and a tick, when the two
        // measurements agree the trade runs with the trend — the
        // high-conviction case, worth spotting without clicking in. Each keeps
        // its OWN colour under the glow: the glow says "these two agree", the
        // colour still says which line you are reading.
        //
        // The emas line alone gets the border and tick — no glow — when it
        // reads `trending` under a `bars: counter`: a pullback inside a trend
        // whose structure is still intact. Not the mirror case; see
        // `stanceBoxLines`.
        //
        // This is NOT the diamond's glow. That one encodes score tier and is
        // left exactly as it was; two glows on one marker answer two different
        // questions (how strong, and do the trend readings agree).
        const agree = stancesAgree(it.counterTrend, it.stackAligned, it.stackOpposed);
        const stance = trendTag(it.counterTrend);
        const stack = stackTag(it.stackAligned, it.stackOpposed);
        // Fifth line: how many EMAs cut the candle's body. Boxed on its own when
        // the count is CLEAR — 0 or 1, the engine's own take/skip line, see
        // `bodyIsClear`. Nothing at all when the engine sent no alignment block.
        const body = bodyTag(it.emasThroughBody);

        // The border around the stance lines, drawn BEFORE either line so the
        // text paints on top of it — the box groups them, it does not sit over
        // them.
        //
        // Gated on `stanceBox` and nothing else, which is a WIDER condition
        // than the glow above: it also boxes the emas line alone when the stack
        // still favours the trade and only the last 10 bars pulled back. See
        // `stanceBoxLines` for why that one case is boxed and its mirror is
        // not. The glow stays on `agree` alone — it is free once the box is
        // here and it does the job at high zoom, where the box is the coarser
        // signal, and lighting up a line the box excludes would undo the
        // distinction the box is drawing.
        if (stance && stack) {
          // Measure in the font the lines are ACTUALLY drawn in, then divide
          // the device scale back out so the geometry comes back in the same
          // pre-scale units as TAG_LINE. Both are measured and both handed
          // over: `bars:` and `emas:` are different widths, and which of them
          // the box has to hold depends on which lines it ends up wrapping.
          //
          // `ctx.font` is the one piece of state these blocks set that the reset
          // below does NOT restore, and that is deliberate rather than an
          // oversight: every fillText in this file sets its own font, align and
          // baseline immediately before drawing, so a font left here reaches
          // nothing. The widened gate above means it is now set on more markers
          // than before — same reasoning, same no-op. Everything else they touch
          // (globalAlpha, fillStyle, strokeStyle, lineWidth, lineCap, lineJoin)
          // IS reset unconditionally; nothing new was added to that list because
          // the body box below paints through the same `paintBox` and so sets
          // exactly the same fields.
          ctx.font = `${TAG_LINE.run.font * hr}px Arial`;
          const runWidth = ctx.measureText(stance.text).width / hr;
          const stackWidth = ctx.measureText(stack.text).width / hr;
          const box = stanceBox(
            it.counterTrend,
            it.stackAligned,
            it.stackOpposed,
            runWidth,
            stackWidth
          );
          if (box) paintBox(ctx, box, x, y + r, hr, vr);
        }

        // The body box — a PEER of the one above, not part of it. Both can be on
        // screen at once and they are separated by `BODY_BOX_GAP`, so the two
        // borders never touch; each wraps only the rows it is making a claim
        // about. Drawn here, before any of the three context lines, so all the
        // grounds land under all the text.
        if (body) {
          ctx.font = `${TAG_LINE.body.font * hr}px Arial`;
          const box = bodyBox(it.emasThroughBody, ctx.measureText(body.text).width / hr);
          if (box) paintBox(ctx, box, x, y + r, hr, vr);
        }
        // Reset UNCONDITIONALLY, outside BOTH `if`s — every field the blocks
        // above touch, whether or not either ran. State left set here bleeds
        // onto the stance text, the botWouldTake ring and every marker drawn
        // after this one in the same frame; a round lineCap on that ring is
        // exactly the kind of change nobody traces back to here. strokeStyle
        // goes back to the marker colour, which is what both later strokes in
        // this item use.
        //
        // fillStyle is in here for the same reason and NOT because anything
        // immediately below depends on it: all three context lines set their own
        // before drawing. It is the box's dark ground, and a translucent navy
        // left on the context is precisely the leak that would be blamed on
        // some later primitive — so it goes back to the marker colour, the
        // same default strokeStyle takes.
        ctx.globalAlpha = 1;
        ctx.fillStyle = colour;
        ctx.strokeStyle = colour;
        ctx.lineWidth = 1;
        ctx.lineCap = 'butt';
        ctx.lineJoin = 'miter';

        if (stance) {
          ctx.globalAlpha = 0.75;
          ctx.fillStyle = stance.colour;
          ctx.font = `${TAG_LINE.run.font * hr}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          if (agree) {
            ctx.shadowColor = stance.colour;
            ctx.shadowBlur = STANCE_GLOW_BLUR * hr;
          }
          // Two tag-heights down, clearing the outcome line the same way that
          // line clears the grade.
          ctx.fillText(stance.text, x, y + r + TAG_LINE.run.y * vr);
          // Cleared unconditionally, not inside the `if`: a shadow left set
          // bleeds onto the stack line, the botWouldTake ring and every marker
          // drawn after this one in the same frame.
          ctx.shadowBlur = 0;
          ctx.shadowColor = 'transparent';
          ctx.globalAlpha = 1;
        }

        // Fourth line: what the EMA 9/21 stack says about the same trade.
        //
        // Two trend lines because they are two different measurements — the run
        // above signs 10 bars of net displacement, this compares two moving
        // averages — and they disagree often enough that the disagreement is
        // the thing worth seeing. Both readings side by side, or neither
        // answers "which one was right this time". Each label names its own
        // source (`bars:` above, `emas:` here) so the pair is readable without
        // remembering which line is which.
        //
        // Same alpha, font, alignment and baseline as the run line above it, so
        // it reads as the same kind of subordinate context rather than a new
        // verdict. Violet when the stack fights the trade, dim slate when it
        // agrees — louder on disagreement, the same way amber/slate is above,
        // and never the outcome line's green/red.
        //
        // Nothing is drawn on an exact tie or when the engine sent no alignment
        // block. Same rule as the line above: no measurement, no stance.
        //
        // Glows on the same `agree` flag as the run line, never on its own —
        // `stancesAgree` is true only when both lines are drawn, so the pair
        // lights up together or not at all.
        if (stack) {
          ctx.globalAlpha = 0.75;
          ctx.fillStyle = stack.colour;
          ctx.font = `${TAG_LINE.stack.font * hr}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          if (agree) {
            ctx.shadowColor = stack.colour;
            ctx.shadowBlur = STANCE_GLOW_BLUR * hr;
          }
          // Three tag-heights down — one below the run stance, which clears the
          // outcome the same way. Deepest line drawn, so it is the one
          // markerReach measures.
          ctx.fillText(stack.text, x, y + r + TAG_LINE.stack.y * vr);
          // Same unconditional clear as the run line: the botWouldTake ring is
          // drawn next and must not inherit a shadow.
          ctx.shadowBlur = 0;
          ctx.shadowColor = 'transparent';
          ctx.globalAlpha = 1;
        }

        // Fifth line: how many of the three EMAs cut this candle's BODY.
        //
        // A third measurement, not a third opinion on the same one. The two
        // lines above ask which way the trend points; this asks whether price is
        // sitting ON the averages, which is chop whichever way they point — so
        // it can read clean under a `counter` stance and muddy under a trending
        // one, and that combination is the reason it is on the chart rather than
        // only in the popup.
        //
        // Same alpha, font, alignment and baseline as the two lines above, so
        // the three read as one block of context under the verdict.
        //
        // It does NOT glow with the stance pair. `agree` is a statement about
        // those two readings agreeing with each other; this line is not one of
        // them, and lighting it on their flag would fold a separate measurement
        // into their claim. Its own box is how it says its piece.
        //
        // Nothing is drawn when the engine sent no alignment block. `body 0/3`
        // is the most reassuring string on the marker and inventing it for an
        // unmeasured bar is exactly the mistake the stance lines were fixed for.
        if (body) {
          ctx.globalAlpha = 0.75;
          ctx.fillStyle = body.colour;
          ctx.font = `${TAG_LINE.body.font * hr}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillText(body.text, x, y + r + TAG_LINE.body.y * vr);
          ctx.globalAlpha = 1;
        }

        // A ring under the diamond marks what the BOT would take. Under, not
        // around, so it never competes with the diamond itself or the tag.
        if (it.botWouldTake) {
          ctx.globalAlpha = 0.9;
          ctx.strokeStyle = colour;
          ctx.lineWidth = 1.2 * hr;
          ctx.beginPath();
          ctx.arc(x, y, r * 1.7, Math.PI * 0.15, Math.PI * 0.85);
          ctx.stroke();
          ctx.globalAlpha = 1;
        }

        // Rejected ones get struck through so they read as "handled, no".
        if (it.state === 'not-ideal') {
          ctx.strokeStyle = colour;
          ctx.lineWidth = 1.2 * hr;
          ctx.beginPath();
          ctx.moveTo(x - r * 1.4, y + r * 1.4);
          ctx.lineTo(x + r * 1.4, y - r * 1.4);
          ctx.stroke();
        }

        ctx.restore();
      }
    });
  }
}

class CandidatePaneView implements IPrimitivePaneView {
  private _source: CandidatePrimitive;
  private _renderer = new CandidatePaneRenderer();

  constructor(source: CandidatePrimitive) {
    this._source = source;
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }

  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.getRendererData());
    return this._renderer;
  }
}

export class CandidatePrimitive implements ISeriesPrimitive<Time> {
  private _markers: CandidateMarker[] = [];
  private _paneView: CandidatePaneView;
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _layout = new MarkerLayoutClient('candidate', (ctx) => this._layoutItems(ctx));
  /** Where the diamonds landed in the last shared layout (markerAt reads it). */
  private _sharedPlaced: { id: string; x: number; y: number; r: number }[] = [];

  constructor() {
    this._paneView = new CandidatePaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  detached(): void {
    this._layout.setAttached(false);
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  /** Join the chart's shared marker layout (null: the fixed OFFSET lane). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    const out: LayoutItem[] = [];
    for (const m of this._markers) {
      const t = Math.floor(m.timestamp / 1000);
      if (t < ctx.fromTime || t > ctx.toTime) continue;
      const x = ctx.timeToX(t);
      const bull = m.direction === 'bull';
      const anchor = ctx.priceToY(bull ? m.high : m.low);
      if (x === null || anchor === null) continue;
      const item = this._renderItem(m, x, 0);
      const b = candidateBlock(item, ctx.measure);
      out.push({
        key: m.id,
        time: t,
        x,
        side: bull ? 'above' : 'below',
        anchorY: anchor,
        w: Math.ceil(b.right - b.left),
        h: Math.ceil(b.up + b.down),
        dx: b.left,
        gap: 3,
        priority: LAYOUT_PRIORITY.candidate,
        color: COLORS[m.state],
        label: `◆ ${TAGS[m.state] || m.state}${m.score !== null ? ` ${Math.round(m.score)}` : ''}`,
        ref: { m, up: b.up },
      });
    }
    return out;
  }

  private _renderItem(m: CandidateMarker, x: number, y: number): RenderItem {
    return {
      x,
      y,
      state: m.state,
      selected: m.selected,
      auto: m.auto === true,
      radius: radiusForScore(m.score),
      score: m.score,
      botWouldTake: m.botWouldTake === true,
      counterTrend: m.counterTrend ?? null,
      stackAligned: m.stackAligned ?? null,
      stackOpposed: m.stackOpposed ?? null,
      emasThroughBody: m.emasThroughBody ?? null,
      simResult: m.simResult ?? null,
      simPoints: m.simPoints ?? null,
    };
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Coordinates recompute from source data each render pass.
  }

  setMarkers(markers: CandidateMarker[]): void {
    this._markers = markers;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  /**
   * Screen-space hit test against the DIAMOND itself, not the whole bar.
   * Named markerAt rather than hitTest: ISeriesPrimitive already declares a
   * hitTest with a different signature, and shadowing it breaks the interface.
   * Returns the marker id under the point, or null.
   *
   * Clicking anywhere on a candle used to open the labeller, which made it
   * impossible to click a bar without the popup appearing. Uses a square
   * bounding box around the diamond with a small touch pad rather than exact
   * rhombus geometry — the diamond is only a few px across and a finger is not.
   */
  markerAt(px: number, py: number): string | null {
    if (!this._series || !this._chart) return null;
    const timeScale = this._chart.timeScale();
    const PAD = 6; // touch slop
    if (this._layout.active()) {
      for (const p of this._sharedPlaced) {
        if (Math.abs(px - p.x) <= p.r + PAD && Math.abs(py - p.y) <= p.r + PAD) return p.id;
      }
      return null;
    }

    for (const m of this._markers) {
      const x = timeScale.timeToCoordinate(Math.floor(m.timestamp / 1000) as Time);
      if (x === null) continue;
      const anchor = m.direction === 'bull' ? m.high : m.low;
      const yAnchor = this._series.priceToCoordinate(anchor);
      if (yAnchor === null) continue;
      const y = m.direction === 'bull' ? yAnchor - OFFSET : yAnchor + OFFSET;
      const r = radiusForScore(m.score) + PAD;
      if (Math.abs(px - x) <= r && Math.abs(py - y) <= r) return m.id;
    }
    return null;
  }

  getRendererData(): RenderItem[] {
    if (!this._series || !this._chart) return [];
    const shared = this._layout.placed();
    if (shared) {
      const items: RenderItem[] = [];
      const hits: { id: string; x: number; y: number; r: number }[] = [];
      for (const p of shared) {
        const { m, up } = p.item.ref as { m: CandidateMarker; up: number };
        const it = this._renderItem(m, p.item.x, p.top + up);
        items.push(it);
        hits.push({ id: m.id, x: it.x, y: it.y, r: it.radius });
      }
      this._sharedPlaced = hits;
      return items;
    }
    const timeScale = this._chart.timeScale();
    const out: RenderItem[] = [];

    for (const m of this._markers) {
      const x = timeScale.timeToCoordinate(Math.floor(m.timestamp / 1000) as Time);
      if (x === null) continue;
      // Bulls get marked above the high, bears below the low, so the glyph
      // never sits on top of the bar it describes.
      const anchor = m.direction === 'bull' ? m.high : m.low;
      const y = this._series.priceToCoordinate(anchor);
      if (y === null) continue;
      out.push({
        x,
        y: m.direction === 'bull' ? y - OFFSET : y + OFFSET,
        state: m.state,
        selected: m.selected,
        auto: m.auto === true,
        radius: radiusForScore(m.score),
        score: m.score,
        botWouldTake: m.botWouldTake === true,
        counterTrend: m.counterTrend ?? null,
        stackAligned: m.stackAligned ?? null,
        stackOpposed: m.stackOpposed ?? null,
        // `null`, never 0, when the engine sent no alignment block — a zero is a
        // measurement and absence is not one.
        emasThroughBody: m.emasThroughBody ?? null,
        // Carried through to the renderer; the draw code reads the item, not
        // the marker.
        simResult: m.simResult ?? null,
        simPoints: m.simPoints ?? null,
      });
    }
    return out;
  }
}
